import React, { useState, useEffect, useRef, CSSProperties, useCallback, useMemo } from "react";

import { Segment, Checkbox, CheckboxProps, List } from "semantic-ui-react";

import { useIntl } from "react-intl";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import DateTextBox from "react-lib/apps/common/DateTextBox";
import SearchBox from "react-lib/apps/common/SearchBox";

type ListChecked = {
  is_selected: boolean | null;
  is_show: boolean | null;
  value: string;
};

type ModalItemFilterProps = {
  setProp?: any;
  buttonLoadKey?: string;
  data?: DateType | Record<string, any>[];
  filters?: string[];
  name: string;
  placeholder?: string;
  style?: CSSProperties;
  textField: string;
  typeDate?: boolean;
  buttonLoadCheck?: any;
  onClose?: () => any;
  onSelect?: (data: DateType | string[]) => any;
};

type DateType = { endDate: string; startDate: string };

const ModalItemFilter: React.FC<ModalItemFilterProps> = (props) => {
  const intl = useIntl();

  const [items, setItems] = useState<ListChecked[]>([]);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);

  const [dateDetail, setDateDetail] = useState<DateType>({ endDate: "", startDate: "" });

  // Ref
  const segmentHoverRef = useRef<any>(false);

  const handleMouseClick = useCallback(() => {
    if (segmentHoverRef.current) {
      return;
    }

    props.onClose?.();

    document.removeEventListener("click", handleMouseClick);

    handleClearState();
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleMouseClick);
  }, []);

  useEffect(() => {
    if (Array.isArray(props.data)) {
      const formattedItems = props.data.map((item) => {
        const value = item[props.textField];

        return {
          is_selected: (props.filters || []).includes(value),
          is_show: true,
          value,
        };
      });

      handleCheckData(formattedItems, null, true);

      return;
    }

    if (props.data) {
      setDateDetail(props.data);
    }
  }, [props.data, props.filters]);

  const arrayUniqueByKey = useMemo(() => {
    const valueSet = new Set();

    // Find unique values
    return items.filter((item) => (valueSet.has(item.value) ? false : valueSet.add(item.value)));
  }, [items]);

  const handleCheckData = (
    data?: ListChecked[],
    isFieldCheck: boolean | null = null,
    isFieldShow: boolean | null = null
  ) => {
    let isShow = false;
    let isCheck = false;

    if (isFieldShow !== null) {
      isShow = true;
    }

    if (isFieldCheck !== null) {
      isCheck = true;
    }

    const list = (data || []).map((acc) => ({
      ...acc,
      is_selected: isCheck ? isFieldCheck : acc.is_selected,
      is_show: isShow ? isFieldShow : acc.is_show,
    }));

    if (list[0] && list.every((item) => item.is_selected)) {
      setIsSelectAll(true);
    } else {
      setIsSelectAll(false);
    }

    setItems(list);
  };

  const handleClearState = () => {
    setItems([]);
    setIsSelectAll(false);
    segmentHoverRef.current = false;
  };

  const handleChange = (value: string, checked: boolean) => {
    const cloneItems = [...items];

    const foundIndex = cloneItems.findIndex((item) => item.value === value);

    cloneItems[foundIndex].is_selected = checked;

    if (cloneItems.every((item) => item.is_selected)) {
      setIsSelectAll(true);
    } else if (isSelectAll) {
      setIsSelectAll(false);
    }

    setItems(cloneItems);
  };

  const handleToggleSelectAll = (
    event: React.FormEvent<HTMLInputElement>,
    { checked }: CheckboxProps
  ) => {
    handleCheckData(items, checked);
  };

  const handleGetSearchOptions = ({ searchText = "" }) => {
    const filterItems = items.map((item) =>
      item.value.includes(searchText) ? { ...item, is_show: true } : { ...item, is_show: false }
    );

    setItems(filterItems);

    return [[], null];
  };

  const handleFilter = () => {
    const newArr = [...items];
    const itemsCheck = newArr.filter((item) => item.is_selected);
    const filterArray = itemsCheck.map((item) => item.value);

    props.onSelect?.(props.typeDate ? dateDetail : filterArray);
  };

  return (
    <Segment
      className="scn-modal-item-filter"
      onClick={() => (segmentHoverRef.current = true)}
      onMouseLeave={() => (segmentHoverRef.current = false)}
      onMouseOver={() => (segmentHoverRef.current = true)}
      style={{
        background: "#f2f2f2",
        position: "fixed",
        width: "300px",
        zIndex: 9,
        ...props.style,
      }}
    >
      <div style={{ color: "rgba(0,0,0,.87)", display: "flex" }}>
        <label style={{ fontWeight: "normal" }}>{intl.formatMessage({ id: "cukey79" })}</label>
        <label style={{ fontWeight: "bold", paddingLeft: "5px" }}>{props.name}</label>
      </div>
      {props.typeDate ? (
        <div>
          <div style={{ alignItems: "center", display: "flex", width: "100%" }}>
            <div style={{ width: "20%" }}>Form</div>
            <div style={{ width: "80%" }}>
              <DateTextBox
                inputStyle={{ width: "90%" }}
                value={dateDetail.startDate}
                onChange={(value: any) => {
                  setDateDetail({ ...dateDetail, startDate: value });
                }}
              />
            </div>
          </div>
          <div style={{ alignItems: "center", display: "flex", margin: "10px 0px", width: "100%" }}>
            <div style={{ width: "20%" }}>To</div>
            <div style={{ width: "80%" }}>
              <DateTextBox
                inputStyle={{ width: "90%" }}
                value={dateDetail.endDate}
                onChange={(value: any) => {
                  setDateDetail({ ...dateDetail, endDate: value });
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div>
          <SearchBox
            inputStyle={{ width: "100%" }}
            minCharacters={0}
            placeholder={props.placeholder}
            onGetSearchOptions={handleGetSearchOptions}
          />
          <Checkbox
            checked={isSelectAll}
            label="Select All"
            onChange={handleToggleSelectAll}
            style={{
              border: "1px solid #e1e1e1",
              fontWeight: "bold",
              padding: "6px",
              textAlign: "left",
              width: "100%",
            }}
          />
          <div style={{ background: "white", height: "130px" }}>
            <List
              style={{
                background: "white",
                fontWeight: "normal",
                height: "120px",
                overflow: "auto",
                padding: "10px 7px",
                textAlign: "left",
              }}
            >
              {arrayUniqueByKey.map((item, index) =>
                item.is_show ? (
                  <List.Item key={index} style={{ paddingBottom: "10px" }}>
                    <Checkbox
                      key={`list-check${index}`}
                      checked={item.is_selected || false}
                      label={item.value}
                      style={{ paddingBottom: 0 }}
                      onChange={(e, { checked = false }) => {
                        handleChange(item.value, checked);
                      }}
                    />
                  </List.Item>
                ) : null
              )}
            </List>
          </div>
        </div>
      )}

      <ButtonLoadCheck
        setProp={props.setProp}
        color="blue"
        paramKey={props.buttonLoadKey}
        title={intl.formatMessage({ id: "cukey78" })}
        fluid
        buttonLoadCheck={props.buttonLoadCheck?.[props.buttonLoadKey || ""]}
        onClick={handleFilter}
      />
    </Segment>
  );
};

ModalItemFilter.displayName = "ModalItemFilter";

export default React.memo(ModalItemFilter);
